import { jsxs, jsx } from 'react/jsx-runtime';
import { createElement, useState, useEffect } from 'react';
import { CustomLink, SubTitle, styled, Caption, Body } from '@teko/ds-desktop';
import { c as convertMegaMenu } from './megaMenu-0ccc961e.js';
import { E as EnhancedImage } from './EnhancedImage-5956213c.js';

const MenuPanel = ({ menu, className, type, }) => {
    var _a, _b;
    const bannerWidth = '14.625rem';
    if (!menu || (!((_a = menu.banner) === null || _a === void 0 ? void 0 : _a.src) && !menu.children.length))
        return null;
    const bannerSrc = (_b = menu.banner) === null || _b === void 0 ? void 0 : _b.src;
    const getMenuColumns = () => {
        let totalCols = 6;
        if (type === 'vertical')
            totalCols--;
        if (bannerSrc)
            totalCols--;
        return totalCols;
    };
    const newMenu = convertMegaMenu(menu, getMenuColumns());
    const col = (item) => {
        return item.map((it, index) => (jsxs(MenuLv2, { children: [jsx(CustomLink, Object.assign({}, it.link, { children: jsx(SubTitle, Object.assign({ level: 1, line: 1, color: "primary500", marginBottom: "s8" }, { children: it.name }), void 0) }), void 0), it.children.map((subItem, index) => (createElement(CustomLink, Object.assign({}, subItem.link, { key: index }),
                    jsx(Text, { children: subItem.name }, void 0))))] }, it.name + `-${index}`)));
    };
    return (jsxs(MenuPanelWrapper, Object.assign({ className: className }, { children: [jsx(MenuItemsWrapper, { children: newMenu.map((menu, index) => (jsx(Column, { children: col(menu.column) }, index))) }, void 0), bannerSrc && (jsx(CustomLink, Object.assign({}, menu.banner.link, { children: jsx(EnhancedImage, { src: bannerSrc, width: bannerWidth, height: "100%", googleImageParams: {
                        width: 400,
                    } }, void 0) }), void 0))] }), void 0));
};
const Text = styled.div(({ theme }) => `
  color: ${theme.colors.textPrimary};
  margin-bottom: 4px;
  &:hover {
    color: ${theme.colors.primary500};
  }
`);
const MenuPanelWrapper = styled.div(({ theme }) => `
  background-color: ${theme.colors.white};
  display: flex;
`);
const MenuItemsWrapper = styled.div(({ theme }) => `
  display: flex;
  flex: 1;
  padding: ${theme.spacing.s12};
`);
const MenuLv2 = styled.div `
  margin-bottom: 1.5rem;
`;
const Column = styled.div `
  width: 25%;
  padding: 0 8px;
`;

const MegaMenu = ({ type, align = 'left', menuItems, }) => {
    if (!menuItems.length)
        return null;
    const [activeMenu, setActiveMenu] = useState(undefined);
    const [endDelay, setEndDelay] = useState(false);
    const verticalMenuWidth = '12.375rem';
    const horizontalMenuHeight = '3rem';
    const megaMenuHeight = '28.5rem';
    useEffect(() => {
        if (activeMenu) {
            setTimeout(() => {
                setEndDelay(true);
            }, 100);
        }
        else {
            setEndDelay(false);
        }
    }, [activeMenu, endDelay]);
    const rennderVerticalMegaMenu = () => {
        return (jsxs(MegaMenuWrapper, Object.assign({ onMouseLeave: () => {
                setActiveMenu(undefined);
            } }, { children: [jsx(VerticalMegaMenu, Object.assign({ width: verticalMenuWidth, height: megaMenuHeight }, { children: menuItems.slice(0, 14).map((item, index) => (createElement(CustomLink, Object.assign({}, item.link, { key: index }),
                        jsxs(VerticalMenuItem, Object.assign({ onMouseEnter: () => {
                                setActiveMenu(item);
                            } }, { children: [jsx(VerticalMenuItemImageMegaMenuWrapper, { children: jsx(EnhancedImage, { src: item.icon }, void 0) }, void 0), jsx(Caption, Object.assign({ level: 2, line: 1, style: { flex: 1 } }, { children: item.name }), void 0)] }), void 0)))) }), void 0), jsx(VerticalMenuPanel, { className: activeMenu && endDelay ? 'active' : '', type: type, verticalMenuWidth: verticalMenuWidth, menu: activeMenu }, void 0)] }), void 0));
    };
    const renderHorizontalMegaMenu = () => {
        return (jsxs(MegaMenuWrapper, Object.assign({ onMouseLeave: () => {
                setActiveMenu(undefined);
            } }, { children: [jsx(HorizontalMegaMenu, Object.assign({ height: horizontalMenuHeight, align: align }, { children: menuItems.slice(0, 14).map((item, index) => (createElement(CustomLink, Object.assign({}, item.link, { key: index }),
                        jsx(HorizontalMenuItem, Object.assign({ height: horizontalMenuHeight, onMouseEnter: () => {
                                setActiveMenu(item);
                            } }, { children: jsx(Body, Object.assign({ level: 2, line: 1, hoverColor: "primary500" }, { children: item.name }), void 0) }), void 0)))) }), void 0), jsx(HorizontalMenuPanel, { type: type, className: activeMenu && endDelay ? 'active' : '', horizontalMenuHeight: horizontalMenuHeight, menu: activeMenu }, void 0)] }), void 0));
    };
    if (type === 'vertical')
        return rennderVerticalMegaMenu();
    return renderHorizontalMegaMenu();
};
const MegaMenuWrapper = styled.div `
  position: relative;
`;
const VerticalMenuPanel = styled(MenuPanel)(({ verticalMenuWidth, theme, }) => `
  position: absolute;
  top: 0;
  margin-left: 0.5rem;
  width: calc(75rem - 0.5rem - ${verticalMenuWidth});
  left: ${verticalMenuWidth};
  min-height: 100%;
  z-index: 980;
  overflow: hidden;
  transition: all 0.2s;
  visibility: hidden;
  opacity: 0;
  border-radius: ${theme.borderRadius.m};
  &.active {
    visibility: visible;
    opacity: 1;
  }
`);
const HorizontalMenuPanel = styled(MenuPanel)(({ horizontalMenuHeight, theme, }) => `
  position: absolute;
  left: 0;
  right: 0;
  top: ${horizontalMenuHeight};
  z-index: 980;
  overflow: hidden;
  visibility: hidden;
  opacity: 0;
  transition: all 0.2s;
  border-bottom-right-radius: ${theme.borderRadius.m};
  border-bottom-left-radius: ${theme.borderRadius.m};
  &.active {
    visibility: visible;
    opacity: 1;
    filter: drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.05));
  }
`);
const VerticalMegaMenu = styled.div(({ theme, width, height }) => `
  border-radius: ${theme.borderRadius.m};
  background-color: ${theme.colors.white};
  position: relative;
  overflow: hidden;
  width: ${width};
  height: ${height};
  z-index: 980;
`);
const HorizontalMegaMenu = styled.div(({ theme, height, align }) => `
  height: ${height};
  background-color: ${theme.colors.white};
  overflow: hidden;
  width: 100%;
  ${align === 'right'
    ? `display: flex;
        justify-content: flex-end;`
    : align === 'space-between'
        ? `display: flex;
        justify-content: space-between;`
        : ''}
`);
const VerticalMenuItem = styled.div(({ theme }) => `
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: start;
  gap: ${theme.spacing.s8};
  padding: 0 ${theme.spacing.s8};
  border-radius: ${theme.borderRadius.m};
  overflow: hidden;
  &:hover {
    background-color: ${theme.colors.primary50};
    color: ${theme.colors.primary500};
  }
`);
const HorizontalMenuItem = styled.div(({ theme, height }) => `
  padding: 0 ${theme.spacing.s12};
  height: ${height};
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  float: left;
  position: relative;
  transition: all 0.3s ease;
  color: ${theme.colors.textPrimary};
  &::after {
    content: '';
    height: 2px;
    background-color: ${theme.colors.primary500};
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    opacity: 0;
    transition: all 0.3s ease;
  }
  &:hover {
    color: ${theme.colors.primary500};
    &::after {
      opacity: 1;
    }
  }
`);
const VerticalMenuItemImageMegaMenuWrapper = styled.div `
  width: 22px;
  height: 22px;
`;

export { MegaMenu as M };
